// @ts-nocheck
import { useCallback, useRef, useEffect } from "react";
import { BetAnchor, DrawnLine, LineDataPoint } from "../types/chart-types";
import type { MaPoint } from "../utils/indicators";
import type { OverlayIndicatorData } from "../types/indicator-types";
import { colorWithOpacity } from "../types/indicator-types";
import {
  calculateNiceInterval,
  calculateNicePriceInterval,
  calculatePaddedPriceRange,
  ctxRoundRect,
  drawSvgFlag,
  easeOutBack,
  formatPriceValue,
  formatTime,
  isSameDayInTz,
  tsToChartTime,
} from "../utils/chart-utils";
import { CrosshairPosition, drawCrosshair } from "../utils/crosshair-util";
import { CHART_CONFIG } from "../utils/chart-config";
import { drawLines } from "../utils/draw-lines";
import { drawBetResultPill, type BetResultPillData } from "../utils/draw-bet-result-pill";
import { resolveEmptySpaceEdge } from "../utils/empty-space";
import { RealBetEvent } from "@/features/common/socket/types";
import type { BetBadgeInfo } from "./CandelstickChart";
import { MA_COLORS } from "../utils/chart-utils";
import { drawAdvancedOverlays } from "../utils/draw-advanced-overlays";
import { getServerNowMs } from "@/shared/utils/serverTime";
import { clampToMonotonicRemainingMs } from "@/shared/utils/monotonicCountdown";

interface LineChartProps {
  data: LineDataPoint[];
  currentViewportTime: number;
  getViewportTime?: () => number;
  windowSize: number;
  isMobile: boolean;
  scrollOffset: number;
  scrollOffsetRef?: React.RefObject<number>;
  baseTimeRef: React.RefObject<number>;
  flagTimeRef: React.RefObject<number>;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  overlayCanvasRef: React.RefObject<HTMLCanvasElement | null>;
  crosshairPosition: CrosshairPosition | null;
  hoveredButton: "call" | "put" | null;
  activeBets?: RealBetEvent[];
  selectedTimePeriodSeconds?: number;
  drawnLines?: DrawnLine[];
  chartRefreshKey: number;
  maDataSets?: MaPoint[][];
  overlayIndicators?: OverlayIndicatorData;
  betBadgesRef?: React.MutableRefObject<BetBadgeInfo[]>;
  lastTickPositionRef?: React.MutableRefObject<{ x: number; y: number } | null>;
  betResultRef?: React.MutableRefObject<BetResultPillData[]>;
  betResultRenderKey?: number;
  isDraggingRef?: React.RefObject<boolean>;
  smoothedLinePriceRef?: React.MutableRefObject<number>;
  smoothedLineTimeRef?: React.MutableRefObject<number>;
  smoothedMinPriceRef?: React.MutableRefObject<number | null>;
  smoothedPriceRangeRef?: React.MutableRefObject<number | null>;
  timezone?: string;
  pricePrecision?: number;
  verticalBias?: number;
  pairRevealStartRef?: React.RefObject<number>;
}

export const LineChart = ({
  data,
  currentViewportTime: currentViewportTimeProp,
  getViewportTime,
  windowSize,
  isMobile,
  scrollOffset,
  scrollOffsetRef,
  baseTimeRef,
  flagTimeRef,
  canvasRef,
  overlayCanvasRef,
  crosshairPosition,
  hoveredButton,
  activeBets = [],
  selectedTimePeriodSeconds,
  drawnLines = [],
  chartRefreshKey,
  maDataSets = [],
  overlayIndicators = {},
  betBadgesRef,
  lastTickPositionRef,
  betResultRef,
  betResultRenderKey,
  isDraggingRef,
  smoothedLinePriceRef,
  smoothedLineTimeRef,
  smoothedMinPriceRef: smoothedMinPriceRefProp,
  smoothedPriceRangeRef: smoothedPriceRangeRefProp,
  timezone,
  pricePrecision,
  verticalBias = 0,
  pairRevealStartRef,
}: LineChartProps) => {
  const canvasSizeRef = useRef({ w: 0, h: 0 });
  const overlaySizeRef = useRef({ w: 0, h: 0 });
  // Smoothed Y-scale refs — prefer parent-provided refs when available (dev), else local.
  const localSmoothedMinPriceRef = useRef<number | null>(null);
  const localSmoothedPriceRangeRef = useRef<number | null>(null);
  const smoothedMinPriceRef = smoothedMinPriceRefProp ?? localSmoothedMinPriceRef;
  const smoothedPriceRangeRef = smoothedPriceRangeRefProp ?? localSmoothedPriceRangeRef;
  const overlayIndicatorsRef = useRef(overlayIndicators);
  overlayIndicatorsRef.current = overlayIndicators;
  const maDataSetsRef = useRef(maDataSets);
  maDataSetsRef.current = maDataSets;
  // Frozen lastDataTime during drag — prevents emptySpace/X-axis shift when new ticks arrive
  const frozenLastDataTimeRef = useRef<number>(0);
  // Tracks whether the previous frame was rendered in live (non-dragging, scrollOffset 0)
  // mode, so we can detect the live → drag/scroll transition and freeze emptySpace cleanly.
  const wasLiveRef = useRef(true);

  const betAnchorRef = useRef<Map<string, BetAnchor>>(new Map());
  const betBirthTimesRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    betAnchorRef.current.clear();
  }, [chartRefreshKey]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        betAnchorRef.current.clear();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  const chartTransformRef = useRef({
    viewportStartTime: 0,
    paddedMinPrice: 0,
    paddedPriceRange: 0,
    emptySpaceFraction: 0,
  });

  const getVisibleRange = useCallback(() => {
    const vt = getViewportTime ? getViewportTime() : currentViewportTimeProp;
    const rightOffsetSpan = windowSize * CHART_CONFIG.padding.emptySpaceFraction;
    const startTime = vt - (windowSize - rightOffsetSpan);
    const drawEndTime = vt + rightOffsetSpan + 1;

    // Binary search for startIndex (first point with time >= startTime)
    let lo = 0,
      hi = data.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (data[mid].time < startTime) lo = mid + 1;
      else hi = mid;
    }
    // Include one off-screen point to the left so the entering line segment is drawn
    // instead of starting with moveTo at the first on-screen point (which breaks the line).
    const startIndex = Math.max(0, lo - 1);

    // Binary search for endIndex (first point with time > drawEndTime)
    lo = startIndex;
    hi = data.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (data[mid].time <= drawEndTime) lo = mid + 1;
      else hi = mid;
    }
    // Include one off-screen point to the right as well. Line chart coordinates
    // are seconds-based, so `drawEndTime + 1` is not enough to catch the next
    // M1 point; without it the segment leading to an off-screen peak disappears
    // while panning.
    const endIndex = Math.min(data.length, lo + 1);

    return { startIndex, endIndex };
  }, [data, currentViewportTimeProp, windowSize]);

  const drawChart = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    const newW = Math.round(rect.width * dpr);
    const newH = Math.round(rect.height * dpr);
    if (canvasSizeRef.current.w !== newW || canvasSizeRef.current.h !== newH) {
      canvas.width = newW;
      canvas.height = newH;
      canvasSizeRef.current = { w: newW, h: newH };
      ctx.scale(dpr, dpr);
    }

    const width = rect.width;
    const height = rect.height;

    if (data.length === 0) {
      ctx.clearRect(0, 0, width, height);
      chartTransformRef.current.paddedMinPrice = 0;
      chartTransformRef.current.paddedPriceRange = 0;
      if (lastTickPositionRef) lastTickPositionRef.current = null;
      return;
    }

    // Pair-switch reveal: fade in the line
    const PAIR_REVEAL_DURATION = 600;
    const pairRevealNow = performance.now();
    const pairRevealElapsed = pairRevealStartRef?.current
      ? Math.max(0, pairRevealNow - pairRevealStartRef.current)
      : PAIR_REVEAL_DURATION;
    const revealAlpha = Math.min(pairRevealElapsed / PAIR_REVEAL_DURATION, 1);

    const currentViewportTime = getViewportTime ? getViewportTime() : currentViewportTimeProp;

    const rawLastDataTime = data.length > 0 ? data[data.length - 1].time : 0;
    // Read scroll state from the ref (matches getViewportTime) so the empty-space edge stays in
    // lockstep with the indicator sub-panels, which also read the ref. The prop lags by a frame.
    const effectiveScrollOffset = scrollOffsetRef?.current ?? scrollOffset;
    const isDragging = isDraggingRef?.current ?? false;
    // Shared edge resolution — keeps the line and every indicator sub-panel on the same time axis.
    const lastDataTime = resolveEmptySpaceEdge({
      isDragging,
      scrollOffset: effectiveScrollOffset,
      rawLastDataTime,
      currentViewportTime,
      frozenRef: frozenLastDataTimeRef,
      wasLiveRef,
    });

    const rightPadding = isMobile ? 50 : 95;
    const maxEmptySpace = Math.round((width - rightPadding) * CHART_CONFIG.padding.emptySpaceFraction);
    const transitionDuration = 5;
    let emptySpace: number;

    if (effectiveScrollOffset === 0) {
      emptySpace = maxEmptySpace;
    } else {
      if (currentViewportTime >= lastDataTime) {
        emptySpace = maxEmptySpace;
      } else if (currentViewportTime >= lastDataTime - transitionDuration) {
        const distanceFromCurrent = lastDataTime - currentViewportTime;
        const progress = distanceFromCurrent / transitionDuration;
        emptySpace = maxEmptySpace * (1 - progress);
      } else {
        emptySpace = 0;
      }
    }

    const stableDrawWidth = width - rightPadding;
    const emptySpaceFraction = emptySpace / stableDrawWidth;
    chartTransformRef.current.emptySpaceFraction = emptySpaceFraction;
    const baseChartWidth = width - rightPadding - emptySpace;

    const bottomPadding = isMobile ? 16 : 30;
    const chartHeight = height - bottomPadding;

    ctx.clearRect(0, 0, width, height);

    const { startIndex, endIndex } = getVisibleRange();
    const visibleData = data.slice(startIndex, endIndex);

    // Calculate dynamic price range from visible data with 10% padding.
    // Include overlay channels so bands stay on-screen.
    const overlayNow = overlayIndicatorsRef.current;
    const maNow = maDataSetsRef.current;
    const overlayRangeExtras: Array<{ high: number; low: number }> = [];
    {
      const t0 = visibleData[0]?.time;
      const t1 = visibleData[visibleData.length - 1]?.time;
      if (typeof t0 === "number" && typeof t1 === "number") {
        const { bb, keltner, donchian, alligator, chaosbands, ichimoku, zigzag } = overlayNow;
        // Envelopes excluded from Y-scale — % bands must not stretch the price axis.
        for (const series of [bb, keltner, donchian, chaosbands]) {
          if (!series) continue;
          for (const p of series) {
            if (p.time < t0 || p.time > t1) continue;
            if ("upper" in p || "lower" in p) {
              const upper = (p as any).upper;
              const lower = (p as any).lower;
              if (Number.isFinite(upper)) overlayRangeExtras.push({ high: upper, low: upper });
              if (Number.isFinite(lower)) overlayRangeExtras.push({ high: lower, low: lower });
            }
          }
        }
        if (alligator) {
          for (const series of [alligator.jaws, alligator.teeth, alligator.lips]) {
            for (const p of series) {
              if (p.time < t0 || p.time > t1) continue;
              overlayRangeExtras.push({ high: p.value, low: p.value });
            }
          }
        }
        if (ichimoku) {
          for (const p of ichimoku) {
            if (p.time < t0 || p.time > t1) continue;
            for (const value of [p.tenkan, p.kijun, p.senkouA, p.senkouB, p.chikou]) {
              if (Number.isFinite(value)) overlayRangeExtras.push({ high: value!, low: value! });
            }
          }
        }
        if (zigzag) {
          for (const p of zigzag) {
            if (p.time >= t0 && p.time <= t1) overlayRangeExtras.push({ high: p.value, low: p.value });
          }
        }
      }
    }
    const lastVisiblePointForScale = visibleData[visibleData.length - 1];
    const latestPointForScale = data[data.length - 1];
    const previousPointForScale = data.length >= 2 ? data[data.length - 2] : null;
    const scaleTimeStep =
      previousPointForScale && latestPointForScale.time > previousPointForScale.time
        ? latestPointForScale.time - previousPointForScale.time
        : 60;
    const scaleSmoothedPrice =
      smoothedLinePriceRef?.current &&
      Number.isFinite(smoothedLinePriceRef.current) &&
      smoothedLinePriceRef.current > 0
        ? smoothedLinePriceRef.current
        : null;
    const scaleSmoothedTime =
      smoothedLineTimeRef?.current &&
      Number.isFinite(smoothedLineTimeRef.current) &&
      smoothedLineTimeRef.current >= latestPointForScale.time
        ? smoothedLineTimeRef.current
        : null;
    const liveDisplayPriceForScale =
      latestPointForScale &&
      lastVisiblePointForScale?.time === latestPointForScale.time &&
      scaleSmoothedPrice !== null &&
      scaleSmoothedTime !== null &&
      scaleSmoothedTime <= latestPointForScale.time + scaleTimeStep * 1.5 &&
      Math.abs(scaleSmoothedPrice - latestPointForScale.price) / Math.max(Math.abs(latestPointForScale.price), 1e-9) <=
        0.05
        ? scaleSmoothedPrice
        : null;
    const visiblePriceRangeData =
      liveDisplayPriceForScale !== null ? [...visibleData, { price: liveDisplayPriceForScale }] : visibleData;

    const { paddedMinPrice: targetMin, paddedPriceRange: targetRange } = calculatePaddedPriceRange({
      visibleData:
        overlayRangeExtras.length > 0 ? [...visiblePriceRangeData, ...overlayRangeExtras] : visiblePriceRangeData,
      chartType: "line",
      verticalBias,
    });

    // Smooth the Y-scale to avoid jumps on new ticks and during dragging.
    // Snap on first call or if target diverges > 50% (e.g. after data reload).
    if (
      smoothedMinPriceRef.current === null ||
      smoothedPriceRangeRef.current === null ||
      Math.abs(targetMin - smoothedMinPriceRef.current) > targetRange * 0.5 ||
      Math.abs(targetRange - smoothedPriceRangeRef.current) > targetRange * 0.5
    ) {
      smoothedMinPriceRef.current = targetMin;
      smoothedPriceRangeRef.current = targetRange;
    } else {
      const k = 0.1; // lerp factor: ~150ms smooth transition at 60fps
      smoothedMinPriceRef.current += (targetMin - smoothedMinPriceRef.current) * k;
      smoothedPriceRangeRef.current += (targetRange - smoothedPriceRangeRef.current) * k;
    }

    if (liveDisplayPriceForScale !== null) {
      const rangeGuard = Math.max(
        targetRange * CHART_CONFIG.padding.yAxisPercentage,
        Math.abs(liveDisplayPriceForScale) * 0.0002,
        1e-9,
      );
      let currentMin = smoothedMinPriceRef.current;
      let currentMax = smoothedMinPriceRef.current + smoothedPriceRangeRef.current;

      if (liveDisplayPriceForScale < currentMin + rangeGuard) {
        currentMin = liveDisplayPriceForScale - rangeGuard;
      }
      if (liveDisplayPriceForScale > currentMax - rangeGuard) {
        currentMax = liveDisplayPriceForScale + rangeGuard;
      }

      smoothedMinPriceRef.current = currentMin;
      smoothedPriceRangeRef.current = Math.max(currentMax - currentMin, 1e-9);
    }

    const paddedMinPrice = smoothedMinPriceRef.current;
    chartTransformRef.current.paddedMinPrice = paddedMinPrice;
    const paddedPriceRange = smoothedPriceRangeRef.current;
    chartTransformRef.current.paddedPriceRange = paddedPriceRange;

    // Use stableDrawWidth (fixed scale) and shift viewportStartTime with emptySpaceFraction.
    // This matches CandlestickChart behaviour: scale stays constant, no stretching on drag.
    const viewportStartTime = currentViewportTime - windowSize * (1 - emptySpaceFraction);
    chartTransformRef.current.viewportStartTime = viewportStartTime;

    const viewportEndTime = currentViewportTime + windowSize * emptySpaceFraction;

    const drawStartX = 0;
    const drawWidth = stableDrawWidth;

    const getX = (dataPoint: LineDataPoint) => {
      const timePosition = dataPoint.time - viewportStartTime;
      const normalizedPosition = timePosition / windowSize;
      return drawStartX + normalizedPosition * drawWidth;
    };

    const getY = (price: number) => {
      return chartHeight - ((price - paddedMinPrice) / paddedPriceRange) * chartHeight;
    };

    // Draw grid aligned with price and time labels
    ctx.strokeStyle = "#374151";
    ctx.lineWidth = 1;
    ctx.globalAlpha = 0.3;

    const gridEndX = width - rightPadding;

    // Horizontal grid lines snapped to nice price intervals (extend to full width so they run under price labels)
    const priceInterval = calculateNicePriceInterval(paddedPriceRange);
    const firstGridPrice = Math.ceil(paddedMinPrice / priceInterval) * priceInterval;
    for (let price = firstGridPrice; price <= paddedMinPrice + paddedPriceRange; price += priceInterval) {
      const y = getY(price);
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width - 25, y);
      ctx.stroke();
    }

    // Vertical grid lines — same interval as time labels
    {
      const gShowSeconds = windowSize <= 180;
      const gViewportTs = baseTimeRef.current + currentViewportTime * 1000;
      const gViewportDate = new Date(gViewportTs);
      const gNow = new Date();
      const gIsDifferentDay = !isSameDayInTz(gViewportDate, gNow, timezone);
      const gLabelW = gIsDifferentDay ? (gShowSeconds ? 120 : 100) : gShowSeconds ? 65 : 45;
      const gMaxLabels = Math.floor(baseChartWidth / gLabelW);
      const gActualLabels = Math.max(3, Math.min(gMaxLabels, 10));
      // Use viewportStartTime (same origin as data points) for aligned grid lines
      const gTimeRange = viewportEndTime - viewportStartTime;
      const gRawInterval = gTimeRange / (gActualLabels - 1);
      const gNiceInterval = calculateNiceInterval(gRawInterval, gShowSeconds);

      const gFirstTick = Math.floor(viewportStartTime / gNiceInterval) * gNiceInterval;

      for (let t = gFirstTick; t <= viewportStartTime + windowSize + gNiceInterval; t += gNiceInterval) {
        const x = ((t - viewportStartTime) / windowSize) * stableDrawWidth;
        if (x >= 0 && x <= gridEndX) {
          ctx.beginPath();
          ctx.moveTo(x, 0);
          ctx.lineTo(x, chartHeight);
          ctx.stroke();
        }
      }
    }

    ctx.globalAlpha = 1;

    // Clip region for chart
    ctx.save();
    ctx.beginPath();

    const clipWidth = baseChartWidth + emptySpace;

    ctx.rect(0, 0, clipWidth, chartHeight);
    ctx.clip();

    // Draw gradient fill and line only if we have enough data
    if (visibleData.length >= 2) {
      // Draw hover zone BELOW yellow fill if button is hovered (desktop only)
      if (hoveredButton && window.innerWidth >= 770) {
        const lastPoint = visibleData[visibleData.length - 1];
        const lastPriceY = getY(lastPoint.price);
        const zoneEndX = width - rightPadding;

        let zoneStartY: number;
        let zoneHeight: number;

        if (hoveredButton === "call") {
          zoneStartY = 0;
          zoneHeight = lastPriceY;
        } else {
          zoneStartY = lastPriceY;
          zoneHeight = chartHeight - lastPriceY;
        }

        const zoneGradient = ctx.createLinearGradient(0, zoneStartY, 0, zoneStartY + zoneHeight);
        if (hoveredButton === "call") {
          zoneGradient.addColorStop(0, "rgba(23, 201, 100, 0)");
          zoneGradient.addColorStop(1, "rgba(23, 201, 100, 0.15)");
        } else {
          zoneGradient.addColorStop(0, "rgba(243, 18, 96, 0.15)");
          zoneGradient.addColorStop(1, "rgba(243, 18, 96, 0)");
        }

        ctx.fillStyle = zoneGradient;
        ctx.fillRect(0, zoneStartY, zoneEndX, zoneHeight);
      }

      ctx.save();
      ctx.globalAlpha = revealAlpha;

      // Draw yellow gradient fill
      const gradient = ctx.createLinearGradient(0, 0, 0, chartHeight);
      gradient.addColorStop(0, "rgba(245, 165, 36, 0.4)");
      gradient.addColorStop(1, "rgba(245, 165, 36, 0.05)");

      ctx.fillStyle = gradient;
      ctx.beginPath();

      const lastVisibleIndex = visibleData.length - 1;
      const hasSmoothedPrice = !!smoothedLinePriceRef?.current;
      const hasSmoothedTime = !!smoothedLineTimeRef?.current;
      // Only apply smoothed position to the last visible point when it IS the actual
      // most recent data point. In history mode the last visible point is an old tick —
      // applying smoothedLineTimeRef (which holds the live tick time) would displace it
      // far to the right and break the line.
      const isAtLiveEdge = data.length > 0 && visibleData[lastVisibleIndex]?.time === data[data.length - 1].time;
      const lastDataPoint = data[data.length - 1];
      const previousDataPoint = data.length >= 2 ? data[data.length - 2] : null;
      const lastPointTimeStep =
        previousDataPoint && lastDataPoint.time > previousDataPoint.time
          ? lastDataPoint.time - previousDataPoint.time
          : 60;
      const smoothedTimeValue =
        hasSmoothedTime && Number.isFinite(smoothedLineTimeRef!.current) ? smoothedLineTimeRef!.current : null;
      const smoothedPriceValue =
        hasSmoothedPrice && Number.isFinite(smoothedLinePriceRef!.current) ? smoothedLinePriceRef!.current : null;
      const canUseSmoothedLivePoint =
        isAtLiveEdge &&
        smoothedTimeValue !== null &&
        smoothedPriceValue !== null &&
        smoothedTimeValue >= lastDataPoint.time &&
        smoothedTimeValue <= lastDataPoint.time + lastPointTimeStep * 1.5 &&
        Math.abs(smoothedPriceValue - lastDataPoint.price) / Math.max(Math.abs(lastDataPoint.price), 1e-9) <= 0.05;

      const drawEndTime = viewportEndTime + 100;
      const smoothedLiveTime = canUseSmoothedLivePoint ? Math.min(smoothedTimeValue, drawEndTime) : null;
      const getDisplayX = (point: LineDataPoint, index: number) => {
        if (index === lastVisibleIndex && smoothedLiveTime !== null) {
          // Cap smoothed time at drawEndTime to prevent the last segment from
          // stretching into the empty zone when viewport is frozen during drag.
          const timePosition = smoothedLiveTime - viewportStartTime;
          return drawStartX + (timePosition / windowSize) * drawWidth;
        }
        return getX(point);
      };
      const getDisplayPrice = (point: LineDataPoint, index: number) =>
        index === lastVisibleIndex && canUseSmoothedLivePoint ? smoothedPriceValue : point.price;

      visibleData.forEach((point, index) => {
        const x = getDisplayX(point, index);
        const y = getY(getDisplayPrice(point, index));

        if (index === 0) {
          ctx.moveTo(x, chartHeight);
          ctx.lineTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });

      const lastX = getDisplayX(visibleData[visibleData.length - 1], lastVisibleIndex);
      ctx.lineTo(lastX, chartHeight);
      ctx.closePath();
      ctx.fill();

      // Draw line
      ctx.strokeStyle = "#F5A524";
      ctx.lineWidth = 1;
      ctx.lineJoin = "round";
      ctx.lineCap = "round";
      ctx.beginPath();

      visibleData.forEach((point, index) => {
        const x = getDisplayX(point, index);
        const y = getY(getDisplayPrice(point, index));

        if (index === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });

      ctx.stroke();
      ctx.restore(); // end pair-switch fade

      // Draw High/Low price labels on chart
      if (visibleData.length > 0) {
        // Find high and low points
        let highPoint = visibleData[0];
        let lowPoint = visibleData[0];

        visibleData.forEach((point) => {
          if (point.price > highPoint.price) highPoint = point;
          if (point.price < lowPoint.price) lowPoint = point;
        });

        // Draw High label
        const highX = getX(highPoint);
        const highY = getY(highPoint.price);
        const highText = formatPriceValue(highPoint.price, pricePrecision);

        ctx.font = `${isMobile ? 9 : 11}px sans-serif`;
        const highTextWidth = ctx.measureText(highText).width;

        const highLabelX = Math.min(Math.max(highX - highTextWidth / 2, 5), stableDrawWidth - highTextWidth - 5);
        const highLabelY = Math.max(highY - 18, 12);

        // Background
        ctx.fillStyle = "#33333380";
        ctx.beginPath();
        ctxRoundRect(ctx, highLabelX - 4, highLabelY - 8, highTextWidth + 8, 16, 3);
        ctx.fill();

        // Text
        ctx.fillStyle = "#737373";
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        ctx.fillText(highText, highLabelX, highLabelY);

        // Draw Low label
        const lowX = getX(lowPoint);
        const lowY = getY(lowPoint.price);
        const lowText = formatPriceValue(lowPoint.price, pricePrecision);
        const lowTextWidth = ctx.measureText(lowText).width;

        const lowLabelX = Math.min(Math.max(lowX - lowTextWidth / 2, 5), stableDrawWidth - lowTextWidth - 5);
        const lowLabelY = Math.min(lowY + 18, chartHeight - 12);

        // Background
        ctx.fillStyle = "#33333380";
        ctx.beginPath();
        ctxRoundRect(ctx, lowLabelX - 4, lowLabelY - 8, lowTextWidth + 8, 16, 3);
        ctx.fill();

        // Text
        ctx.fillStyle = "#737373";
        ctx.fillText(lowText, lowLabelX, lowLabelY);
      }
    }
    // ── Overlay indicators ────────────────────────────────────────────────────
    const {
      bb,
      supertrend,
      donchian,
      sar,
      envelopes,
      keltner,
      alligator,
      fractal,
      chaosbands,
      ichimoku,
      zigzag,
      envelopesStyle,
      keltnerStyle,
      alligatorStyle,
      fractalStyle,
      chaosbandsStyle,
      ichimokuStyle,
      zigzagStyle,
      bbStyle,
      supertrendStyle,
      donchianStyle,
      sarStyle,
    } = overlayNow;
    drawAdvancedOverlays({
      ctx,
      ichimoku,
      zigzag,
      ichimokuStyle,
      zigzagStyle,
      viewportStartTime,
      viewportEndTime,
      windowSize,
      drawStartX,
      drawWidth,
      getY,
    });

    if (bb && bb.length >= 2) {
      const styles = bbStyle?.lines;
      const vis = bb.filter((p) => p.time >= viewportStartTime - windowSize * 0.1 && p.time <= viewportEndTime + 10);
      if (vis.length >= 2) {
        const toXY = (p: any, key: string) => ({
          x: drawStartX + ((p.time - viewportStartTime) / windowSize) * drawWidth,
          y: getY(p[key]),
        });
        const fill = bbStyle?.fills?.background;
        if (fill?.visible) {
          ctx.save();
          ctx.globalAlpha = fill.opacity;
          ctx.fillStyle = fill.color;
          ctx.beginPath();
          vis.forEach((p, i) => {
            const { x, y } = toXY(p, "upper");
            i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
          });
          [...vis].reverse().forEach((p) => {
            const { x, y } = toXY(p, "lower");
            ctx.lineTo(x, y);
          });
          ctx.closePath();
          ctx.fill();
          ctx.restore();
        }
        const drawBand = (pts: { x: number; y: number }[], color: string, dash: number[], lineWidth = 1) => {
          ctx.save();
          ctx.strokeStyle = color;
          ctx.lineWidth = lineWidth;
          ctx.setLineDash(dash);
          ctx.lineJoin = "round";
          ctx.beginPath();
          pts.forEach(({ x, y }, i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
          ctx.stroke();
          ctx.restore();
        };
        if (styles?.top?.visible !== false)
          drawBand(
            vis.map((p) => toXY(p, "upper")),
            styles?.top?.color ?? "rgba(167,139,250,0.8)",
            [],
            styles?.top?.width,
          );
        if (styles?.middle?.visible !== false)
          drawBand(
            vis.map((p) => toXY(p, "middle")),
            styles?.middle?.color ?? "rgba(167,139,250,0.5)",
            [4, 4],
            styles?.middle?.width,
          );
        if (styles?.bottom?.visible !== false)
          drawBand(
            vis.map((p) => toXY(p, "lower")),
            styles?.bottom?.color ?? "rgba(167,139,250,0.8)",
            [],
            styles?.bottom?.width,
          );
      }
    }

    if (supertrend && supertrend.length >= 2) {
      const vis = supertrend.filter(
        (p) => p.time >= viewportStartTime - windowSize * 0.1 && p.time <= viewportEndTime + 10,
      );
      if (vis.length >= 2) {
        ctx.save();
        ctx.lineWidth = supertrendStyle?.lines?.up?.width ?? 2;
        ctx.lineJoin = "round";
        ctx.lineCap = "round";
        for (let i = 1; i < vis.length; i++) {
          const p0 = vis[i - 1],
            p1 = vis[i];
          const x0 = drawStartX + ((p0.time - viewportStartTime) / windowSize) * drawWidth;
          const x1 = drawStartX + ((p1.time - viewportStartTime) / windowSize) * drawWidth;
          const trendStyle = p1.trend === 1 ? supertrendStyle?.lines?.up : supertrendStyle?.lines?.down;
          if (trendStyle?.visible === false) continue;
          ctx.strokeStyle = trendStyle?.color ?? (p1.trend === 1 ? "#22c55e" : "#ef4444");
          ctx.lineWidth = trendStyle?.width ?? 2;
          ctx.beginPath();
          ctx.moveTo(x0, getY(p0.value));
          ctx.lineTo(x1, getY(p1.value));
          ctx.stroke();
        }
        ctx.restore();
      }
    }

    if (donchian && donchian.length >= 2) {
      const styles = donchianStyle?.lines;
      const vis = donchian.filter(
        (p) => p.time >= viewportStartTime - windowSize * 0.1 && p.time <= viewportEndTime + 10,
      );
      if (vis.length >= 2) {
        const toXY = (p: any, key: string) => ({
          x: drawStartX + ((p.time - viewportStartTime) / windowSize) * drawWidth,
          y: getY(p[key]),
        });
        const drawBand = (pts: { x: number; y: number }[], color: string, lineWidth = 1) => {
          ctx.save();
          ctx.strokeStyle = color;
          ctx.lineWidth = lineWidth;
          ctx.lineJoin = "round";
          ctx.beginPath();
          pts.forEach(({ x, y }, i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
          ctx.stroke();
          ctx.restore();
        };
        const fill = donchianStyle?.fills?.background;
        if (fill?.visible) {
          ctx.save();
          ctx.globalAlpha = fill.opacity;
          ctx.fillStyle = fill.color;
          ctx.beginPath();
          vis.forEach((p, i) => {
            const { x, y } = toXY(p, "upper");
            i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
          });
          [...vis].reverse().forEach((p) => {
            const { x, y } = toXY(p, "lower");
            ctx.lineTo(x, y);
          });
          ctx.closePath();
          ctx.fill();
          ctx.restore();
        }
        if (styles?.top?.visible !== false)
          drawBand(
            vis.map((p) => toXY(p, "upper")),
            styles?.top?.color ?? "rgba(56,189,248,0.7)",
            styles?.top?.width,
          );
        if (styles?.middle?.visible !== false)
          drawBand(
            vis.map((p) => toXY(p, "middle")),
            styles?.middle?.color ?? "rgba(56,189,248,0.35)",
            styles?.middle?.width,
          );
        if (styles?.bottom?.visible !== false)
          drawBand(
            vis.map((p) => toXY(p, "lower")),
            styles?.bottom?.color ?? "rgba(56,189,248,0.7)",
            styles?.bottom?.width,
          );
      }
    }

    if (sar && sar.length >= 1) {
      const sarLineStyle = sarStyle?.lines?.main;
      const vis =
        sarLineStyle?.visible === false
          ? []
          : sar.filter((p) => p.time >= viewportStartTime - windowSize * 0.1 && p.time <= viewportEndTime + 10);
      const dotR = Math.max(1.5, Math.min(3, drawWidth / windowSize / 2));
      vis.forEach((p) => {
        const x = drawStartX + ((p.time - viewportStartTime) / windowSize) * drawWidth;
        ctx.save();
        ctx.fillStyle = sarLineStyle?.color ?? (p.trend === 1 ? "#22c55e" : "#ef4444");
        ctx.beginPath();
        ctx.arc(x, getY(p.value), dotR, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      });
    }

    if (fractal && fractal.length >= 1) {
      const frStyle = fractalStyle ?? {
        up: { visible: true, color: "#86DA0E" },
        down: { visible: true, color: "#FF0000" },
      };
      const frVisible = fractal.filter(
        (p) => p.time >= viewportStartTime - windowSize * 0.1 && p.time <= viewportEndTime + 10,
      );
      const size = Math.max(4, Math.min(7, drawWidth / windowSize / 1.5));
      const gap = size * 0.6;
      frVisible.forEach((p) => {
        const x = drawStartX + ((p.time - viewportStartTime) / windowSize) * drawWidth;
        if (frStyle.up.visible && p.up != null) {
          const y = getY(p.up) - gap;
          ctx.save();
          ctx.fillStyle = frStyle.up.color;
          ctx.beginPath();
          ctx.moveTo(x, y - size);
          ctx.lineTo(x - size * 0.75, y);
          ctx.lineTo(x + size * 0.75, y);
          ctx.closePath();
          ctx.fill();
          ctx.restore();
        }
        if (frStyle.down.visible && p.down != null) {
          const y = getY(p.down) + gap;
          ctx.save();
          ctx.fillStyle = frStyle.down.color;
          ctx.beginPath();
          ctx.moveTo(x, y + size);
          ctx.lineTo(x - size * 0.75, y);
          ctx.lineTo(x + size * 0.75, y);
          ctx.closePath();
          ctx.fill();
          ctx.restore();
        }
      });
    }

    if (chaosbands && chaosbands.length >= 2) {
      const cbVisible = chaosbands.filter(
        (p) => p.time >= viewportStartTime - windowSize * 0.1 && p.time <= viewportEndTime + 10,
      );
      if (cbVisible.length >= 2) {
        const st = chaosbandsStyle ?? {
          up: { visible: true, color: "#25E154", width: 1, opacity: 1 },
          down: { visible: true, color: "#FF6C58", width: 1, opacity: 1 },
          background: { visible: true, color: "#57A1D0", opacity: 0.15 },
        };
        const toXY = (p: { time: number; upper?: number; lower?: number }, key: "upper" | "lower") => ({
          x: drawStartX + ((p.time - viewportStartTime) / windowSize) * drawWidth,
          y: getY(p[key]!),
        });
        const bothVisible = cbVisible.filter((p) => p.upper != null && p.lower != null);
        const upperPts = bothVisible.map((p) => toXY(p, "upper"));
        const lowerPts = bothVisible.map((p) => toXY(p, "lower"));
        const upperLinePts = cbVisible.filter((p) => p.upper != null).map((p) => toXY(p, "upper"));
        const lowerLinePts = cbVisible.filter((p) => p.lower != null).map((p) => toXY(p, "lower"));
        const strokeBand = (pts: { x: number; y: number }[]) => {
          ctx.beginPath();
          ctx.moveTo(pts[0].x, pts[0].y);
          for (let i = 1; i < pts.length; i++) {
            ctx.lineTo(pts[i].x, pts[i].y);
          }
          ctx.stroke();
        };
        if (st.background?.visible && upperPts.length >= 2) {
          ctx.save();
          ctx.beginPath();
          ctx.moveTo(upperPts[0].x, upperPts[0].y);
          for (let i = 1; i < upperPts.length; i++) {
            ctx.lineTo(upperPts[i].x, upperPts[i].y);
          }
          for (let i = lowerPts.length - 1; i >= 0; i--) {
            ctx.lineTo(lowerPts[i].x, lowerPts[i].y);
          }
          ctx.closePath();
          ctx.fillStyle = colorWithOpacity(st.background.color, st.background.opacity);
          ctx.fill();
          ctx.restore();
        }
        if (st.up.visible && upperLinePts.length >= 2) {
          ctx.save();
          ctx.strokeStyle = colorWithOpacity(st.up.color, st.up.opacity ?? 1);
          ctx.lineWidth = st.up.width ?? 1;
          ctx.lineJoin = "miter";
          ctx.lineCap = "butt";
          strokeBand(upperLinePts);
          ctx.restore();
        }
        if (st.down.visible && lowerLinePts.length >= 2) {
          ctx.save();
          ctx.strokeStyle = colorWithOpacity(st.down.color, st.down.opacity ?? 1);
          ctx.lineWidth = st.down.width ?? 1;
          ctx.lineJoin = "miter";
          ctx.lineCap = "butt";
          strokeBand(lowerLinePts);
          ctx.restore();
        }
      }
    }

    if (envelopes && envelopes.length >= 2) {
      const vis = envelopes.filter(
        (p) => p.time >= viewportStartTime - windowSize * 0.1 && p.time <= viewportEndTime + 10,
      );
      if (vis.length >= 2) {
        const toXY = (p: any, key: string) => ({
          x: drawStartX + ((p.time - viewportStartTime) / windowSize) * drawWidth,
          y: getY(p[key]),
        });
        const st = envelopesStyle;
        const top = st?.top ?? { visible: true, color: "#F1C40F", width: 1, opacity: 0.95 };
        const mid = st?.middle ?? { visible: true, color: "#00FFCC", width: 1, opacity: 0.55 };
        const bot = st?.bottom ?? { visible: true, color: "#86DA0E", width: 1, opacity: 0.95 };
        const bg = st?.background;
        const upperPts = vis.map((p) => toXY(p, "upper"));
        const lowerPts = vis.map((p) => toXY(p, "lower"));
        if (bg?.visible) {
          ctx.save();
          ctx.beginPath();
          upperPts.forEach(({ x, y }, i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
          for (let i = lowerPts.length - 1; i >= 0; i--) ctx.lineTo(lowerPts[i].x, lowerPts[i].y);
          ctx.closePath();
          ctx.fillStyle = colorWithOpacity(bg.color, bg.opacity);
          ctx.fill();
          ctx.restore();
        }
        const drawBand = (pts: { x: number; y: number }[], ls: typeof top, dash: number[] = []) => {
          if (!ls.visible) return;
          ctx.save();
          ctx.strokeStyle = colorWithOpacity(ls.color, ls.opacity);
          ctx.lineWidth = ls.width;
          ctx.setLineDash(dash);
          ctx.lineJoin = "round";
          ctx.beginPath();
          pts.forEach(({ x, y }, i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
          ctx.stroke();
          ctx.restore();
        };
        drawBand(upperPts, top);
        drawBand(
          vis.map((p) => toXY(p, "middle")),
          mid,
          [4, 4],
        );
        drawBand(lowerPts, bot);
      }
    }

    if (keltner && keltner.length >= 2) {
      const vis = keltner.filter(
        (p) => p.time >= viewportStartTime - windowSize * 0.1 && p.time <= viewportEndTime + 10,
      );
      if (vis.length >= 2) {
        const toXY = (p: any, key: string) => ({
          x: drawStartX + ((p.time - viewportStartTime) / windowSize) * drawWidth,
          y: getY(p[key]),
        });
        const st = keltnerStyle;
        const top = st?.top ?? { visible: true, color: "#00FFCC", width: 1, opacity: 0.8 };
        const mid = st?.middle ?? { visible: true, color: "#00FFCC", width: 1, opacity: 0.5 };
        const bot = st?.bottom ?? { visible: true, color: "#00FFCC", width: 1, opacity: 0.8 };
        const bg = st?.background;
        const upperPts = vis.map((p) => toXY(p, "upper"));
        const lowerPts = vis.map((p) => toXY(p, "lower"));

        if (bg?.visible) {
          ctx.save();
          ctx.beginPath();
          upperPts.forEach(({ x, y }, i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
          for (let i = lowerPts.length - 1; i >= 0; i--) ctx.lineTo(lowerPts[i].x, lowerPts[i].y);
          ctx.closePath();
          ctx.fillStyle = colorWithOpacity(bg.color, bg.opacity);
          ctx.fill();
          ctx.restore();
        }

        const drawBand = (pts: { x: number; y: number }[], ls: typeof top, dash: number[]) => {
          if (!ls.visible) return;
          ctx.save();
          ctx.strokeStyle = colorWithOpacity(ls.color, ls.opacity);
          ctx.lineWidth = ls.width;
          ctx.setLineDash(dash);
          ctx.lineJoin = "round";
          ctx.beginPath();
          pts.forEach(({ x, y }, i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
          ctx.stroke();
          ctx.restore();
        };
        drawBand(upperPts, top, []);
        drawBand(
          vis.map((p) => toXY(p, "middle")),
          mid,
          [4, 4],
        );
        drawBand(lowerPts, bot, []);
      }
    }

    if (alligator) {
      const st = alligatorStyle;
      const lips = st?.top ?? { visible: true, color: "#00FFCC", width: 1, opacity: 1 };
      const teeth = st?.middle ?? { visible: true, color: "#E67E22", width: 1, opacity: 1 };
      const jaws = st?.bottom ?? { visible: true, color: "#2256FF", width: 1, opacity: 1 };
      const drawAlligatorLine = (series: { time: number; value: number }[] | undefined, ls: typeof lips) => {
        if (!ls.visible || !series || series.length < 2) return;
        const visible = series.filter(
          (p) => p.time >= viewportStartTime - windowSize * 0.1 && p.time <= viewportEndTime + 10,
        );
        if (visible.length < 2) return;
        ctx.save();
        ctx.strokeStyle = colorWithOpacity(ls.color, ls.opacity);
        ctx.lineWidth = ls.width;
        ctx.lineJoin = "round";
        ctx.lineCap = "round";
        ctx.beginPath();
        visible.forEach((p, i) => {
          const x = drawStartX + ((p.time - viewportStartTime) / windowSize) * drawWidth;
          const y = getY(p.value);
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        });
        ctx.stroke();
        ctx.restore();
      };
      drawAlligatorLine(alligator.jaws, jaws);
      drawAlligatorLine(alligator.teeth, teeth);
      drawAlligatorLine(alligator.lips, lips);
    }

    // Draw MA lines
    maNow.forEach((maData, idx) => {
      const maLineStyle = overlayNow.maStyle?.lines?.[`period${idx + 1}`];
      if (maLineStyle?.visible === false) return;
      if (maData.length < 2) return;
      const maVisible = maData.filter(
        (p) => p.time >= viewportStartTime - windowSize * 0.1 && p.time <= viewportEndTime + 10,
      );
      if (maVisible.length < 2) return;
      ctx.save();
      ctx.strokeStyle = maLineStyle?.color ?? MA_COLORS[idx] ?? "#38bdf8";
      ctx.globalAlpha = maLineStyle?.opacity ?? 1;
      ctx.lineWidth = maLineStyle?.width ?? 1.5;
      ctx.lineJoin = "round";
      ctx.lineCap = "round";
      ctx.beginPath();
      maVisible.forEach((p, i) => {
        const x = drawStartX + ((p.time - viewportStartTime) / windowSize) * drawWidth;
        const y = getY(p.value);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();
      ctx.restore();
    });

    ctx.restore();

    // Draw price labels
    ctx.fillStyle = "#9ca3af";
    ctx.font = `${isMobile ? 10 : 12}px sans-serif`;
    ctx.textAlign = "left";
    ctx.textBaseline = "bottom";

    const priceLabelX = width - rightPadding + 10;
    const priceLabelInterval = calculateNicePriceInterval(paddedPriceRange);
    const firstLabelPrice = Math.ceil(paddedMinPrice / priceLabelInterval) * priceLabelInterval;
    for (let price = firstLabelPrice; price <= paddedMinPrice + paddedPriceRange; price += priceLabelInterval) {
      const y = getY(price) - 4;
      ctx.fillText(formatPriceValue(price, pricePrecision), priceLabelX, y);
    }

    // Draw time labels
    // startTime aligned with data coordinate origin (viewportStartTime)
    const startTime = viewportStartTime;
    const endTime = currentViewportTime;

    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillStyle = "#9ca3af";

    const showSeconds = windowSize <= 180;

    // Check if viewport is showing a different day to adjust label width for dates
    const currentViewportTimestamp = baseTimeRef.current + currentViewportTime * 1000;
    const viewportDate = new Date(currentViewportTimestamp);
    const now = new Date();
    const isDifferentDay = !isSameDayInTz(viewportDate, now, timezone);

    const labelWidth = isDifferentDay ? (showSeconds ? 120 : 100) : showSeconds ? 65 : 45;
    const maxLabels = Math.floor(baseChartWidth / labelWidth);
    const actualLabels = Math.max(3, Math.min(maxLabels, 10));

    const timeRange = endTime - startTime; // = windowSize * (1 - emptySpaceFraction)
    const rawInterval = Math.max(timeRange, windowSize) / (actualLabels - 1);
    const niceInterval = calculateNiceInterval(rawInterval, showSeconds);

    // Future extends by emptySpaceFraction * windowSize — exactly fills the empty zone
    const futureTimeRange = emptySpaceFraction * windowSize;
    const futureEndTime = endTime + futureTimeRange;

    const firstTick = Math.floor(startTime / niceInterval) * niceInterval;
    let lastLabelRightEdge = -Infinity;

    for (let timeIndex = firstTick; timeIndex <= futureEndTime; timeIndex += niceInterval) {
      if (timeIndex < startTime - niceInterval) continue;

      const timestamp = baseTimeRef.current + timeIndex * 1000;
      const date = new Date(timestamp);
      const timeStr = formatTime(date, showSeconds, Date.now(), false, timezone);

      // Unified coordinate: same origin as data points — no split formula for past/future
      const x = ((timeIndex - viewportStartTime) / windowSize) * stableDrawWidth;

      if (x >= 0 && x <= width) {
        const textW = ctx.measureText(timeStr).width;
        const labelLeft = x - textW / 2;
        if (labelLeft > lastLabelRightEdge + 8) {
          ctx.fillText(timeStr, x, chartHeight + 8);
          lastLabelRightEdge = x + textW / 2;
        }
      }
    }
  }, [
    data,
    getVisibleRange,
    windowSize,
    isMobile,
    scrollOffset,
    currentViewportTimeProp,
    baseTimeRef,
    canvasRef,
    hoveredButton,
    pricePrecision,
    verticalBias,
  ]);

  const drawOverlay = useCallback(() => {
    const canvas = overlayCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    const newW = Math.round(rect.width * dpr);
    const newH = Math.round(rect.height * dpr);
    if (overlaySizeRef.current.w !== newW || overlaySizeRef.current.h !== newH) {
      canvas.width = newW;
      canvas.height = newH;
      overlaySizeRef.current = { w: newW, h: newH };
      ctx.scale(dpr, dpr);
    }

    const width = rect.width;
    const height = rect.height;

    if (data.length === 0) {
      ctx.clearRect(0, 0, width, height);
      if (betBadgesRef) betBadgesRef.current = [];
      if (lastTickPositionRef) lastTickPositionRef.current = null;
      return;
    }

    const currentViewportTime = getViewportTime ? getViewportTime() : currentViewportTimeProp;

    const rightPadding = isMobile ? 50 : 95;

    const stableDrawWidth = width - rightPadding;
    const emptySpaceFraction = chartTransformRef.current.emptySpaceFraction;
    const gridEndX = width - rightPadding;
    const overlayViewportStartTime = chartTransformRef.current.viewportStartTime;

    if (!Number.isFinite(overlayViewportStartTime)) return;

    const getTimeX = (relativeTimeSecs: number) =>
      ((relativeTimeSecs - overlayViewportStartTime) / windowSize) * stableDrawWidth;

    const bottomPadding = isMobile ? 16 : 30;
    const chartHeight = height - bottomPadding;

    ctx.clearRect(0, 0, width, height);

    if (betBadgesRef) betBadgesRef.current = [];
    const flagAbsoluteTime = flagTimeRef.current;

    const x = getTimeX(flagAbsoluteTime);

    // Show flag whenever it's within the visible viewport bounds.
    // Guard against NaN/Infinity when futureTimeRange collapses to 0 while scrolled.
    if (x !== undefined && Number.isFinite(x) && x >= 0 && x <= width - rightPadding) {
      const totalSeconds = selectedTimePeriodSeconds || 0;
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      const countdownText = `${minutes}:${seconds.toString().padStart(2, "0")}`;

      // Draw white circle with countdown at the top
      drawSvgFlag(ctx, x, 1, countdownText, isMobile);

      // Draw vertical line below the circle
      const circleRadius = isMobile ? 16 : 20;
      const lineStartY = 1 + circleRadius * 2 + 8;
      ctx.strokeStyle = "#FFFFFF";
      ctx.lineWidth = 1;
      ctx.globalAlpha = 0.5;
      ctx.beginPath();
      ctx.moveTo(x, lineStartY);
      ctx.lineTo(x, chartHeight);
      ctx.stroke();
      ctx.globalAlpha = 1;
    }

    if (activeBets && activeBets.length > 0) {
      // Calculate price range for Y positioning (same as for last point)
      const { startIndex, endIndex } = getVisibleRange();
      const visibleData = data.slice(startIndex, endIndex);

      if (visibleData.length > 0) {
        // Use smoothed Y-scale to keep bet lines aligned with the chart
        const paddedMinPrice = chartTransformRef.current.paddedMinPrice;
        const paddedPriceRange = chartTransformRef.current.paddedPriceRange;

        if (!chartTransformRef.current.paddedPriceRange) return;
        const getPriceY = (price: number) => {
          return chartHeight - ((price - paddedMinPrice) / paddedPriceRange) * chartHeight;
        };

        const lastDP = data.length > 0 ? data[data.length - 1] : null;
        const stableChartRef = lastDP?.time ?? 0;
        const isAtLiveEdge =
          lastDP && visibleData.length > 0 && visibleData[visibleData.length - 1]?.time === lastDP.time;
        const liveEnd = overlayViewportStartTime + windowSize;
        const liveTime =
          isAtLiveEdge &&
          smoothedLineTimeRef?.current &&
          Number.isFinite(smoothedLineTimeRef.current) &&
          smoothedLineTimeRef.current > 0
            ? Math.min(smoothedLineTimeRef.current, liveEnd)
            : (lastDP?.time ?? 0);
        const livePrice = smoothedLinePriceRef?.current > 0 ? smoothedLinePriceRef.current : (lastDP?.price ?? 0);

        const priceOnLine = (t: number) => {
          if (!lastDP || data.length === 0) return null;
          if (isAtLiveEdge && livePrice > 0 && liveTime > 0 && t >= lastDP.time) {
            if (t >= liveTime) return livePrice;
            const span = liveTime - lastDP.time;
            return span <= 0 ? livePrice : lastDP.price + ((t - lastDP.time) / span) * (livePrice - lastDP.price);
          }
          if (t <= data[0].time) return data[0].price;
          if (t >= lastDP.time) return lastDP.price;
          let lo = 0;
          let hi = data.length - 1;
          while (lo < hi - 1) {
            const mid = (lo + hi) >> 1;
            if (data[mid].time <= t) lo = mid;
            else hi = mid;
          }
          const p0 = data[lo];
          const p1 = data[hi];
          const span = p1.time - p0.time;
          return p0.price + (span > 0 ? (t - p0.time) / span : 0) * (p1.price - p0.price);
        };

        const candleSpacing = stableDrawWidth / windowSize;
        const badgeGap = Math.min(32, candleSpacing * 0.4);

        const activeBetIds = new Set<string>();
        const activeBetCreatedAts = new Set<string>();

        activeBets.forEach((bet) => {
          if (!bet.createdAt || !bet.targetTime || !bet.betType) {
            return;
          }

          const betCreatedMs = new Date(bet.createdAt).getTime();
          const betTargetMs = new Date(bet.targetTime).getTime();
          const betPrice = typeof bet.entryPrice === "string" ? parseFloat(bet.entryPrice) : bet.entryPrice || 0;
          const betKey = bet.uiKey ?? bet.betId;
          activeBetIds.add(betKey);
          activeBetCreatedAts.add(bet.createdAt);

          let anchor = betAnchorRef.current.get(betKey);
          if (!anchor) {
            let chartTime = tsToChartTime(betCreatedMs, data, stableChartRef);
            const isLiveBet =
              isAtLiveEdge && lastDP && (chartTime >= lastDP.time - 0.5 || getServerNowMs() - betCreatedMs <= 5000);
            if (isLiveBet) chartTime = liveTime;
            const targetTime = chartTime + (betTargetMs - betCreatedMs) / 1000;
            let birthTime = betBirthTimesRef.current.get(bet.createdAt);
            if (birthTime === undefined) {
              birthTime = performance.now();
              betBirthTimesRef.current.set(bet.createdAt, birthTime);
            }
            anchor = {
              chartTime,
              targetTime,
              price: betPrice > 0 ? betPrice : (priceOnLine(chartTime) ?? 0),
              birthTime,
            };
            // Only cache once the bet's candle is within loaded data. If the bet predates
            // all loaded data, tsToChartTime extrapolates inaccurately — skip caching so
            // the position is recalculated each frame as loadMoreHistory brings it into range.
            const betInDataRange = data.length === 0 || betCreatedMs >= data[0].timestamp;
            if (isLiveBet || betInDataRange) {
              betAnchorRef.current.set(betKey, anchor);
            }
          }

          const rawWallClockRemainingMs = Math.max(0, betTargetMs - getServerNowMs());
          const wallClockRemainingMs = clampToMonotonicRemainingMs(betKey, rawWallClockRemainingMs);
          const createdRelativeTime = anchor.chartTime;
          const targetRelativeTime = anchor.targetTime;

          const lineStartX = Math.max(0, getTimeX(createdRelativeTime));
          const targetX = getTimeX(targetRelativeTime);

          if (Number.isFinite(lineStartX) && Number.isFinite(targetX)) {
            const lineY = getPriceY(anchor.price);
            const isCopyTrade = !!bet.isCopyTradingTrade;

            // Determine line color based on bet type
            const lineColor = bet.betType === "put" ? "#F31260" : "#17C964";

            // Calculate time remaining
            const timeRemainingSeconds = Math.ceil(wallClockRemainingMs / 1000);
            const minutes = Math.floor(timeRemainingSeconds / 60);
            const secs = timeRemainingSeconds % 60;
            const timeText = `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;

            {
              // Write badge info to ref for HTML overlay rendering
              if (betBadgesRef) {
                const bgColor = bet.betType === "put" ? "#F31260" : "#17C964";
                const padH = isMobile ? 8 : 10;
                const padV = 4;
                const amountFontSize = isMobile ? 11 : 14;
                const timeFontSize = isMobile ? 9 : 11;
                const arrowHalf = isMobile ? 5 : 6;
                const arrowGap = 5;
                const lineGap = isMobile ? 2 : 3;
                ctx.save();
                ctx.font = `bold ${amountFontSize}px sans-serif`;
                const amountW = ctx.measureText(`$${bet.amount}`).width;
                ctx.font = `${timeFontSize}px sans-serif`;
                const timeW = ctx.measureText(timeText).width;
                ctx.restore();
                const line1ContentW = arrowHalf * 2 + arrowGap + amountW;
                const badgeW = Math.max(line1ContentW, timeW) + padH * 2;
                const copyLabelH = isCopyTrade ? (isMobile ? 14 : 16) : 0;
                const badgeH = padV * 2 + amountFontSize + lineGap + timeFontSize + copyLabelH;
                const badgeX = lineStartX - badgeGap - badgeW - 8;
                const badgeY = lineY - badgeH / 2;
                if (isFinite(badgeX) && isFinite(badgeY) && badgeX + badgeW >= 0 && badgeX <= width) {
                  betBadgesRef.current.push({
                    id: bet.betId || String(lineStartX),
                    createdAt: bet.createdAt,
                    x: badgeX,
                    y: badgeY,
                    w: badgeW,
                    h: badgeH,
                    bgColor,
                    isCall: bet.betType !== "put",
                    amount: `$${bet.amount}`,
                    timeText,
                    isCopyTrade,
                  });
                }
              }

              // Draw short dashed line (badgeGap px) to the left of lineStartX — badge connector
              ctx.save();
              ctx.strokeStyle = lineColor;
              ctx.lineWidth = 1;
              ctx.globalAlpha = 0.5;
              ctx.setLineDash([3, 3]);
              ctx.beginPath();
              ctx.moveTo(Math.max(0, lineStartX - badgeGap), lineY);
              ctx.lineTo(lineStartX, lineY);
              ctx.stroke();
              ctx.setLineDash([]);
              ctx.restore();

              // Draw horizontal line from badge to target
              if (lineStartX < targetX) {
                ctx.save();
                ctx.strokeStyle = lineColor;
                ctx.lineWidth = 1;
                ctx.globalAlpha = 1;
                ctx.beginPath();
                ctx.moveTo(lineStartX, lineY);
                ctx.lineTo(targetX, lineY);
                ctx.stroke();
                ctx.restore();

                // White dots at each end of the solid line
                const dotRadius = isMobile ? 2.5 : 3;
                ctx.save();
                ctx.fillStyle = "#FFFFFF";
                ctx.globalAlpha = 1;
                ctx.beginPath();
                ctx.arc(lineStartX, lineY, dotRadius, 0, Math.PI * 2);
                ctx.fill();
                ctx.beginPath();
                ctx.arc(targetX, lineY, dotRadius, 0, Math.PI * 2);
                ctx.fill();
                ctx.restore();
              }

              // Draw dashed line from target to right edge
              ctx.save();
              ctx.strokeStyle = lineColor;
              ctx.lineWidth = 1;
              ctx.globalAlpha = 0.5;
              ctx.setLineDash([4, 4]);
              ctx.beginPath();
              ctx.moveTo(targetX, lineY);
              ctx.lineTo(width - rightPadding, lineY);
              ctx.stroke();
              ctx.setLineDash([]);
              ctx.restore();
            }

            // Draw controller circle anchored to the bet's expiry — show whenever in bounds.
            if (isFinite(targetX) && targetX >= 0 && targetX <= width - rightPadding) {
              const dropElapsed = performance.now() - anchor.birthTime;
              const dropProgress = Math.min(dropElapsed / 600, 1);
              const dropEased = dropProgress >= 1 ? 1 : easeOutBack(dropProgress);
              const flagAlpha = Math.min(1, dropProgress / 0.6);
              const flagCircleRadius = isMobile ? 16 : 20;
              const circleCenterY = 1 + flagCircleRadius + 4;

              ctx.save();
              ctx.globalAlpha = flagAlpha;
              ctx.translate(targetX, circleCenterY);
              ctx.scale(dropEased, dropEased);
              ctx.translate(-targetX, -circleCenterY);
              drawSvgFlag(ctx, targetX, 1, timeText, isMobile, true, isCopyTrade);
              ctx.restore();

              const flagLineStartY = 1 + flagCircleRadius * 2 + 8;
              ctx.save();
              ctx.globalAlpha = flagAlpha;
              if (isCopyTrade) {
                const grad = ctx.createLinearGradient(targetX, flagLineStartY, targetX, chartHeight);
                grad.addColorStop(0, "#F89A09");
                grad.addColorStop(1, "rgba(248,154,9,0.15)");
                ctx.strokeStyle = grad;
              } else {
                ctx.strokeStyle = "#FFFFFF";
                ctx.globalAlpha = flagAlpha * 0.5;
              }
              ctx.lineWidth = 1;
              ctx.beginPath();
              ctx.moveTo(targetX, flagLineStartY);
              ctx.lineTo(targetX, chartHeight);
              ctx.stroke();
              ctx.restore();
            }
          }
        });

        for (const key of betAnchorRef.current.keys()) {
          if (!activeBetIds.has(key)) betAnchorRef.current.delete(key);
        }
        for (const key of betBirthTimesRef.current.keys()) {
          if (!activeBetCreatedAts.has(key)) betBirthTimesRef.current.delete(key);
        }
      }
    }

    // Draw pulsating dot with dashed line and yellow price badge for last data point
    if (data.length > 0) {
      const lastPoint = data[data.length - 1];

      const { startIndex, endIndex } = getVisibleRange();
      const visibleData = data.slice(startIndex, endIndex);
      const isLiveEdgeVisible = visibleData.at(-1)?.time === lastPoint.time;

      if (visibleData.length > 0 && isLiveEdgeVisible) {
        // Use smoothed Y-scale from drawChart to keep dot aligned with line
        const paddedMinPrice = chartTransformRef.current.paddedMinPrice;
        const paddedPriceRange = chartTransformRef.current.paddedPriceRange;

        if (!chartTransformRef.current.paddedPriceRange) return;

        // Use same coordinate system as drawChart to keep dot aligned with line end
        const viewportStartTime = overlayViewportStartTime;

        const getY = (price: number) => {
          return chartHeight - ((price - paddedMinPrice) / paddedPriceRange) * chartHeight;
        };

        // Use smoothed time/price so the dot glides smoothly to the new tick position
        // instead of jumping when a new data point is added. Cap at drawEndTime so the
        // live dot stays aligned with the price line when the viewport is frozen during drag.
        const overlayDrawEndTime = overlayViewportStartTime + windowSize;
        const previousPoint = data.length >= 2 ? data[data.length - 2] : null;
        const lastPointTimeStep =
          previousPoint && lastPoint.time > previousPoint.time ? lastPoint.time - previousPoint.time : 60;
        const smoothedTime =
          smoothedLineTimeRef?.current && Number.isFinite(smoothedLineTimeRef.current)
            ? smoothedLineTimeRef.current
            : null;
        const smoothedPrice =
          smoothedLinePriceRef?.current && smoothedLinePriceRef.current > 0
            ? smoothedLinePriceRef.current
            : null;
        const canUseSmoothedLivePoint =
          smoothedTime !== null &&
          smoothedPrice !== null &&
          smoothedTime >= lastPoint.time &&
          smoothedTime <= lastPoint.time + lastPointTimeStep * 1.5 &&
          Math.abs(smoothedPrice - lastPoint.price) / Math.max(Math.abs(lastPoint.price), 1e-9) <= 0.05;
        const liveTime = canUseSmoothedLivePoint ? Math.min(smoothedTime, overlayDrawEndTime) : lastPoint.time;
        const livePrice = canUseSmoothedLivePoint ? smoothedPrice : lastPoint.price;
        const lastX = (() => {
          const timePosition = liveTime - viewportStartTime;
          return (timePosition / windowSize) * stableDrawWidth;
        })();
        const lastY = getY(livePrice);

        // Update last tick position for bet result overlay
        if (lastTickPositionRef) {
          lastTickPositionRef.current = { x: lastX, y: lastY };
        }

        const isVisible = lastX >= -20 && lastX <= stableDrawWidth;

        if (isVisible) {
          // Hover zones are drawn in drawChart (below yellow fill)

          ctx.strokeStyle = "#FFFFFF";
          ctx.lineWidth = 1;
          ctx.globalAlpha = 0.6;
          ctx.beginPath();
          ctx.moveTo(lastX, lastY);
          ctx.lineTo(width - rightPadding, lastY);
          ctx.stroke();
          ctx.globalAlpha = 1;

          // Draw yellow price badge on Y axis (shows smoothed display price)
          const priceText = formatPriceValue(livePrice, pricePrecision);
          ctx.font = `${isMobile ? 10 : 12}px sans-serif`;
          ctx.textBaseline = "middle";
          ctx.textAlign = "center";

          const textMetrics = ctx.measureText(priceText);
          const badgeWidth = textMetrics.width + 24;
          const badgeHeight = isMobile ? 16 : 18;
          const badgeX = width - rightPadding + rightPadding / 3.5;
          const badgeY = lastY;

          // Draw badge background
          ctx.fillStyle = "#FFFFFF";
          ctx.beginPath();
          ctxRoundRect(ctx, badgeX - badgeWidth / 2, badgeY - badgeHeight / 2, badgeWidth, badgeHeight, 3);
          ctx.fill();

          // Draw badge text
          ctx.fillStyle = "#000000";
          ctx.fillText(priceText, badgeX, badgeY);

          // Draw pulsating dot with animation
          const time = Date.now() / 1000;
          const pulseScale = 0.8 + Math.sin(time * 3) * 0.2; // Pulsate between 0.6 and 1.0
          const pulseOpacity = 0.6 + Math.sin(time * 3) * 0.4; // Opacity between 0.2 and 1.0

          ctx.globalAlpha = 1;
          const glowRadius = 26 * pulseScale;
          const glow = ctx.createRadialGradient(lastX, lastY, 0, lastX, lastY, glowRadius);
          glow.addColorStop(0, `rgba(255,255,255,${pulseOpacity * 0.8})`);
          glow.addColorStop(0.5, `rgba(255,255,255,${pulseOpacity * 0.25})`);
          glow.addColorStop(1, "rgba(255,255,255,0)");
          ctx.fillStyle = glow;
          ctx.beginPath();
          ctx.arc(lastX, lastY, glowRadius, 0, Math.PI * 2);
          ctx.fill();

          // Solid center dot
          ctx.fillStyle = "#FFFFFF";
          ctx.beginPath();
          ctx.arc(lastX, lastY, 3, 0, Math.PI * 2);
          ctx.fill();

          ctx.globalAlpha = 1;
        }
        if (!isVisible && lastTickPositionRef) {
          lastTickPositionRef.current = null;
        }
      } else if (lastTickPositionRef) {
        lastTickPositionRef.current = null;
      }
    }

    // Draw user-added lines
    if (drawnLines.length > 0) {
      const { startIndex, endIndex } = getVisibleRange();
      // Use exact visible range (no +20) so price range matches drawChart
      const visibleData = data.slice(startIndex, endIndex);
      // Match the coordinate system used by drawChart (stableDrawWidth + shifted xDomainStart)
      // so that shape handles align with their visual positions and hit-testing works correctly.
      const linesXDomainStart = currentViewportTime - windowSize * (1 - emptySpaceFraction);
      drawLines({
        ctx,
        drawnLines,
        visibleData,
        allData: data,
        chartType: "line",
        chartHeight,
        baseChartWidth: stableDrawWidth,
        gridEndX,
        xDomainStart: linesXDomainStart,
        windowSize,
        pricePrecision,
        smoothedMinPrice: chartTransformRef.current.paddedMinPrice ?? undefined,
        smoothedPriceRange: chartTransformRef.current.paddedPriceRange ?? undefined,
      });
    }

    // Draw settled result clouds on canvas to avoid extra DOM/RAF pressure.
    const betResults = betResultRef?.current;
    if (betResults && betResults.length > 0 && data.length > 0) {
      const { startIndex, endIndex } = getVisibleRange();
      const visibleData = data.slice(startIndex, endIndex);
      if (visibleData.length > 0 && chartTransformRef.current.paddedPriceRange) {
        const paddedMinPricePill = chartTransformRef.current.paddedMinPrice;
        const paddedPriceRangePill = chartTransformRef.current.paddedPriceRange;

        for (const betResult of betResults) {
          // Last data point whose period contains closePriceTs
          let bestPoint = data[data.length - 1];
          for (const p of data) {
            if (p.timestamp <= betResult.closePriceTs) bestPoint = p;
            else break;
          }

          const pillX = getTimeX(bestPoint.time);
          const pillY = chartHeight - ((betResult.price - paddedMinPricePill) / paddedPriceRangePill) * chartHeight;

          if (Number.isFinite(pillX) && Number.isFinite(pillY)) {
            drawBetResultPill(ctx, pillX, pillY, betResult.type, betResult.amount);
          }
        }
      }
    }

    if (crosshairPosition) {
      drawCrosshair({
        ctx,
        position: crosshairPosition,
        canvasWidth: width,
        canvasHeight: height,
        chartOffsetX: 0,
        rightPadding,
        bottomPadding,
        isMobile,
        windowSize,
        timezone,
        pricePrecision,
      });
    }
  }, [
    data,
    windowSize,
    isMobile,
    scrollOffset,
    currentViewportTimeProp,
    flagTimeRef,
    overlayCanvasRef,
    crosshairPosition,
    baseTimeRef,
    getVisibleRange,
    hoveredButton,
    activeBets,
    drawnLines,
    pricePrecision,
    maDataSets,
    overlayIndicators,
    getViewportTime,
    scrollOffsetRef,
    verticalBias,
    timezone,
    betBadgesRef,
    lastTickPositionRef,
    betResultRef,
    betResultRenderKey,
    isDraggingRef,
    smoothedLinePriceRef,
    smoothedLineTimeRef,
    selectedTimePeriodSeconds,
  ]);

  return { drawChart, drawOverlay };
};
